import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  BarChart3,
  Users2,
  UserCircle2,
  Building2,
  FileDown,
  Calendar,
} from "lucide-react";
import {
  exportTeamReportPDF,
  exportCollaboratorReportPDF,
  exportClientReportPDF,
  exportClientInvoicePDF,
  fmtBRL,
} from "../lib/financeReportPDF";
import { CollabTable, fmtTime, fmtDate } from "../components/financeReportTables";
import { SendMenu } from "../components/SendMenu";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { logError } from "../lib/logger";

const TAB_DEFS = [
  { id: "team", labelKey: "Por Equipe", icon: Users2, color: "blue" },
  { id: "collab", labelKey: "Por Colaborador", icon: UserCircle2, color: "emerald" },
  { id: "client", labelKey: "Por Cliente", icon: Building2, color: "amber" },
];

function defaultRange(kind) {
  const now = new Date();
  if (kind === "week") {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { from: d.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
  }
  if (kind === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: d.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
  }
  if (kind === "year") {
    return {
      from: `${now.getFullYear()}-01-01`,
      to: now.toISOString().slice(0, 10),
    };
  }
  return { from: "", to: now.toISOString().slice(0, 10) };
}

export default function FinanceReports() {
  const { t } = useI18n();
  const [tab, setTab] = useState("team");
  const [range, setRange] = useState(defaultRange("month"));
  const [data, setData] = useState({ team: [], collab: [], client: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?from=${range.from}&to=${range.to}`;
      const [t, u, c] = await Promise.all([
        api.get(`/reports/by-team${q}`),
        api.get(`/reports/by-collaborator${q}`),
        api.get(`/reports/by-client${q}`),
      ]);
      setData({ team: t.data, collab: u.data, client: c.data });
    } catch (e) {
      logError("Failed to load reports:", e);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const periodLabel = `${range.from || t("início")} → ${range.to || t("hoje")}`;

  const totals = useMemo(() => {
    return {
      team_revenue: data.team.reduce((s, r) => s + (r.revenue || 0), 0),
      team_count: data.team.reduce((s, r) => s + (r.count || 0), 0),
      collab_payout: data.collab.reduce((s, r) => s + (r.to_receive || 0), 0),
      client_total: data.client.reduce((s, r) => s + (r.total_paid || 0), 0),
    };
  }, [data]);

  async function handlePrint() {
    const label = `${range.from} a ${range.to}`;
    if (tab === "team") await exportTeamReportPDF(data.team, label);
    else if (tab === "collab") await exportCollaboratorReportPDF(data.collab, label);
    else await exportClientReportPDF(data.client, label);
  }

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Financeiro")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <BarChart3 size={26} /> {t("Relatórios")}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center w-full sm:w-auto">
          {["week", "month", "year", "custom"].map((k) => (
            <button
              key={k}
              data-testid={`range-${k}`}
              onClick={() => k !== "custom" && setRange(defaultRange(k))}
              className="px-3 py-2 rounded-full text-xs font-semibold border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 min-h-[40px]"
            >
              {{ week: t("Última semana"), month: t("Este mês"), year: t("Este ano"), custom: t("Custom") }[k]}
            </button>
          ))}
          <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <DatePickerUS
              testId="range-from"
              value={range.from}
              onChange={(v) => setRange({ ...range, from: v })}
            />
            <span className="text-slate-400 self-center hidden xs:inline">→</span>
            <DatePickerUS
              testId="range-to"
              value={range.to}
              onChange={(v) => setRange({ ...range, to: v })}
            />
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label={t("Faturamento")} value={fmtBRL(totals.team_revenue)} color="blue" />
        <Stat label={t("Limpezas")} value={totals.team_count} color="slate" />
        <Stat label={t("A pagar (colaboradores)")} value={fmtBRL(totals.collab_payout)} color="emerald" />
        <Stat
          label={t("Lucro estimado")}
          value={fmtBRL(totals.team_revenue - totals.collab_payout)}
          color="amber"
        />
      </div>

      {/* Tabs */}
      <div className="cm-card overflow-hidden mb-6">
        <div className="flex flex-wrap border-b border-slate-200">
          {TAB_DEFS.map((tdef) => {
            const Icon = tdef.icon;
            const active = tab === tdef.id;
            return (
              <button
                key={tdef.id}
                data-testid={`tab-${tdef.id}`}
                onClick={() => setTab(tdef.id)}
                className={`flex-1 min-w-[150px] px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  active
                    ? "border-blue-600 text-blue-700 bg-blue-50"
                    : "border-transparent text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Icon size={16} className="inline mr-1" />
                {t(tdef.labelKey)}
              </button>
            );
          })}
        </div>

        <div className="p-4 flex flex-wrap items-center gap-2 justify-between bg-slate-50 border-b border-slate-200">
          <div className="text-xs text-slate-600 flex items-center gap-2">
            <Calendar size={12} /> {periodLabel}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              data-testid="print-pdf-btn"
              onClick={handlePrint}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <FileDown size={14} className="mr-1" /> {t("Imprimir PDF")}
            </Button>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-12 text-slate-400">{t("Carregando…")}</div>
          ) : tab === "team" ? (
            <TeamTable rows={data.team} />
          ) : tab === "collab" ? (
            <CollabTable
              rows={data.collab}
              period={periodLabel}
              periodFrom={range.from}
              periodTo={range.to}
              EmptyTable={EmptyTable}
            />
          ) : (
            <ClientTable rows={data.client} period={periodLabel} />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, color = "blue" }) {
  const palette = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  }[color];
  return (
    <div className={`cm-card p-4 border-2 ${palette}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function EmptyTable({ children }) {
  return (
    <div className="text-center py-12 text-slate-400 text-sm">{children}</div>
  );
}

function TeamTable({ rows }) {
  const { t } = useI18n();
  if (!rows.length)
    return <EmptyTable>{t("Sem limpezas finalizadas no período.")}</EmptyTable>;
  return (
    <table className="w-full text-sm" data-testid="team-report-table">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
        <tr>
          <th className="text-left p-3">{t("Equipe")}</th>
          <th className="text-right p-3">{t("Casas")}</th>
          <th className="text-right p-3">{t("Faturado")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr
            key={t.team_id}
            className="border-b border-slate-100 hover:bg-slate-50"
          >
            <td className="p-3">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: t.team_color || "#475569" }}
                />
                <strong>{t.team_name}</strong>
              </div>
            </td>
            <td className="p-3 text-right">{t.count}</td>
            <td className="p-3 text-right font-semibold text-blue-700">
              {fmtBRL(t.revenue)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function ClientInvoiceActions({ row, period }) {
  const { t } = useI18n();
  const [clientCache, setClientCache] = useState(null);

  async function downloadInvoice() {
    try {
      await exportClientInvoicePDF(row, period);
      toast.success(t("Invoice gerado"));
    } catch (e) {
      logError(e);
      toast.error(t("Erro ao gerar invoice"));
    }
  }

  async function getClient() {
    if (clientCache) return clientCache;
    try {
      const r = await api.get(`/clients/${row.client_id}`);
      setClientCache(r.data);
      return r.data;
    } catch (e) {
      logError(e);
      return null;
    }
  }

  // Lazy client fetch for SendMenu (which needs phone/email)
  const [enriched, setEnriched] = useState({
    id: row.client_id,
    name: row.name,
    phone: "",
    email: "",
    address: "",
    total_value: row.total_paid,
  });
  React.useEffect(() => {
    let alive = true;
    getClient().then((c) => {
      if (alive && c) {
        setEnriched({
          id: c.id,
          name: c.name,
          phone: c.phone || "",
          email: c.email || "",
          address: c.address || "",
          total_value: row.total_paid,
        });
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.client_id]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={downloadInvoice}
        data-testid={`client-${row.client_id}-invoice-pdf`}
        title={t("Baixar invoice em PDF")}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800"
      >
        <FileText size={12} /> {t("Invoice PDF")}
      </button>
      <SendMenu
        kind="Invoice"
        record={enriched}
        testIdPrefix={`client-${row.client_id}-invoice-send`}
      />
    </div>
  );
}

function ClientTable({ rows, period }) {
  const { t } = useI18n();
  if (!rows.length)
    return <EmptyTable>{t("Nenhum cliente com limpezas finalizadas.")}</EmptyTable>;
  return (
    <div className="space-y-6" data-testid="client-report-table">
      {rows.map((c) => (
        <div key={c.client_id} className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">
                {c.address}, {c.city} {c.state} · {c.count} {c.count === 1 ? t("limpeza") : t("limpezas")} · {c.frequency || "—"}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-slate-500 uppercase tracking-wider">{t("Total pago")}</div>
                <div className="font-bold text-lg text-amber-700">{fmtBRL(c.total_paid)}</div>
              </div>
              <ClientInvoiceActions row={c} period={period} />
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-xs uppercase text-slate-500 border-b border-slate-100">
              <tr>
                <th className="text-left p-2.5">{t("Data")}</th>
                <th className="text-right p-2.5">{t("Início")}</th>
                <th className="text-right p-2.5">{t("Fim")}</th>
                <th className="text-left p-2.5">{t("Equipe")}</th>
                <th className="text-right p-2.5">{t("Valor")}</th>
              </tr>
            </thead>
            <tbody>
              {c.jobs.map((j) => (
                <tr
                  key={j.id}
                  data-testid={`client-${c.client_id}-job-${j.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="p-2.5 text-slate-700">{fmtDate(j.date)}</td>
                  <td className="p-2.5 text-right text-slate-600 font-mono">{fmtTime(j.start_time)}</td>
                  <td className="p-2.5 text-right text-slate-600 font-mono">{fmtTime(j.end_time)}</td>
                  <td className="p-2.5 text-slate-700">{j.team_name || "—"}</td>
                  <td className="p-2.5 text-right font-semibold text-amber-700">{fmtBRL(j.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
